import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { Box } from '@mui/material';
import AssignmentTurnedInRoundedIcon from '@mui/icons-material/AssignmentTurnedInRounded';
import BoltRoundedIcon from '@mui/icons-material/BoltRounded';
import ReportProblemRoundedIcon from '@mui/icons-material/ReportProblemRounded';
import SolarPowerRoundedIcon from '@mui/icons-material/SolarPowerRounded';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import { DataGrid } from '@mui/x-data-grid';

import MonthRangePicker from '../../components/monthRangePicker';
import MultiSelectFilter from '../../components/multiSelectFilter';
import PageHeader from '../../components/pageHeader';
import { PageIntro } from '../../components/ui';
import {
    formatBackendDatePtBr,
    formatMonthParam,
    getCurrentMonthStart,
    getPreviousDayMonthStart
} from '../../utils/dateUtils';
import { fetchFlask } from '../../utils/fetchFlask';
import { formatDecimal, formatInteger } from '../../utils/numberUtils';
import FalhasBissChart from './falhasBissChart';
import FalhasBissCompare from './falhasBissCompare';
import '../css/falhasBiss.css';

const allOptionValue = 'all';
const minMonth = new Date(2025, 0, 1);

const buildOptions = (items) => [
    { value: allOptionValue, label: 'Todos' },
    ...items.map((item) => ({
        value: String(item.id),
        label: item.nome
    }))
];

const getSelectedPayload = (selectedValues) => (
    selectedValues.includes(allOptionValue) ? [] : selectedValues
);

const hasActiveSelection = (selectedValues) => !selectedValues.includes(allOptionValue);

const preserveSelfFilteredOptions = ({
    incomingOptions,
    previousOptions,
    selectedEmpresas,
    selectedInversores,
    selectedSites,
    selectedUsinas
}) => ({
    usinas: hasActiveSelection(selectedUsinas) && previousOptions.usinas.length > 0
        ? previousOptions.usinas
        : incomingOptions.usinas,
    inversores: hasActiveSelection(selectedInversores) && previousOptions.inversores.length > 0
        ? previousOptions.inversores
        : incomingOptions.inversores,
    sites: hasActiveSelection(selectedSites) && previousOptions.sites.length > 0
        ? previousOptions.sites
        : incomingOptions.sites,
    empresas: hasActiveSelection(selectedEmpresas) && previousOptions.empresas.length > 0
        ? previousOptions.empresas
        : incomingOptions.empresas
});

const mapChartRows = (rows) => rows.map((row) => ({
    data: row.data,
    geracao: Number(row.geracao),
    perda: Number(row.perda),
    estimado: Number(row.estimado)
}));

const mapChartInverterRows = (rows) => rows.map((row) => ({
    data: row.data,
    inversorId: row.inversor_id,
    geracao: Number(row.geracao),
    perda: Number(row.perda),
    estimado: Number(row.estimado)
}));

const mapInverterInfo = (rows) => rows.map((row) => ({
    inversorId: row.inversor_id,
    inversor: row.inversor,
    serial: row.serial
}));

const mapFailureRows = (rows) => rows.map((row, index) => ({
    id: row.id,
    data: row.data,
    usina: row.usina,
    inversorId: row.inversor_id,
    inversor: row.inversor,
    serial: row.serial,
    categoria: row.categoria,
    observacao: row.observacao,
    rowIndex: index
}));

const mapAlertRows = (rows) => rows.map((row, index) => ({
    id: `${row.data}-${row.usina_id}-${row.inversor_id}`,
    data: row.data,
    usina: row.usina,
    inversorId: row.inversor_id,
    inversor: row.inversor,
    serial: row.serial,
    alertas: row.alertas,
    rowIndex: index
}));

const buildSummary = ({ alertas, chartRows, falhas }) => {
    const totals = chartRows.reduce((acc, row) => ({
        geracao: acc.geracao + row.geracao,
        perda: acc.perda + row.perda,
        estimado: acc.estimado + row.estimado
    }), {
        geracao: 0,
        perda: 0,
        estimado: 0
    });

    return {
        ...totals,
        falhas: falhas.length,
        alertas: alertas.length
    };
};

const failureColumns = [
    {
        field: 'data',
        headerName: 'Data',
        width: 130,
        align: 'center',
        headerAlign: 'center',
        renderCell: (params) => formatBackendDatePtBr(params.value, params.value)
    },
    {
        field: 'usina',
        headerName: 'Usina',
        flex: 1,
        minWidth: 220,
        renderCell: (params) => (
            <span className='falhas-biss-plant-cell'>
                <SolarPowerRoundedIcon />
                {params.value}
            </span>
        )
    },
    {
        field: 'inversor',
        headerName: 'Inversor',
        minWidth: 160,
        flex: 0.7
    },
    {
        field: 'serial',
        headerName: 'Serial',
        minWidth: 170,
        flex: 0.8
    },
    {
        field: 'categoria',
        headerName: 'Categoria',
        minWidth: 170,
        flex: 0.8
    },
    {
        field: 'observacao',
        headerName: 'Observação',
        minWidth: 320,
        flex: 1.4
    }
];

const alertColumns = [
    {
        field: 'data',
        headerName: 'Data',
        width: 130,
        align: 'center',
        headerAlign: 'center',
        renderCell: (params) => formatBackendDatePtBr(params.value, params.value)
    },
    {
        field: 'usina',
        headerName: 'Usina',
        flex: 1,
        minWidth: 220,
        renderCell: (params) => (
            <span className='falhas-biss-plant-cell'>
                <SolarPowerRoundedIcon />
                {params.value}
            </span>
        )
    },
    {
        field: 'inversor',
        headerName: 'Inversor',
        minWidth: 180,
        flex: 0.8
    },
    {
        field: 'serial',
        headerName: 'Serial',
        minWidth: 170,
        flex: 0.8
    },
    {
        field: 'alertas',
        headerName: 'Alertas',
        minWidth: 260,
        flex: 1.2
    }
];

const FalhasBI = () => {
    const [dataInicio, setDataInicio] = useState(getCurrentMonthStart);
    const [dataFim, setDataFim] = useState(getCurrentMonthStart);
    const [selectedUsinas, setSelectedUsinas] = useState([allOptionValue]);
    const [selectedInversores, setSelectedInversores] = useState([allOptionValue]);
    const [selectedSites, setSelectedSites] = useState([allOptionValue]);
    const [selectedEmpresas, setSelectedEmpresas] = useState([allOptionValue]);
    const [filterOptions, setFilterOptions] = useState({
        usinas: [],
        inversores: [],
        sites: [],
        empresas: []
    });
    const [chartRows, setChartRows] = useState([]);
    const [chartInverterRows, setChartInverterRows] = useState([]);
    const [inverterInfo, setInverterInfo] = useState([]);
    const [viewMode, setViewMode] = useState('consolidado');
    const [falhas, setFalhas] = useState([]);
    const [alertas, setAlertas] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [initialDataLoaded, setInitialDataLoaded] = useState(false);
    const initialFetchStarted = useRef(false);

    const usinaOptions = useMemo(() => buildOptions(filterOptions.usinas), [filterOptions.usinas]);
    const inversorOptions = useMemo(() => buildOptions(filterOptions.inversores), [filterOptions.inversores]);
    const siteOptions = useMemo(() => buildOptions(filterOptions.sites), [filterOptions.sites]);
    const empresaOptions = useMemo(() => buildOptions(filterOptions.empresas), [filterOptions.empresas]);
    const summary = useMemo(() => buildSummary({ alertas, chartRows, falhas }), [alertas, chartRows, falhas]);
    // O comparativo exige uma usina ou inversores no filtro; sem isso o backend não devolve dados por inversor.
    const hasCompareScope = hasActiveSelection(selectedUsinas) || hasActiveSelection(selectedInversores);
    const incluirInversores = viewMode === 'comparar' && hasCompareScope;

    const buscarFalhas = useCallback(async ({
        dataFimValue,
        dataInicioValue,
        empresasValue,
        incluirInversoresValue,
        inversoresValue,
        sitesValue,
        usinasValue
    }) => {
        setIsLoading(true);

        const body = JSON.stringify({
            data_inicio: formatMonthParam(dataInicioValue),
            data_fim: formatMonthParam(dataFimValue),
            usina_ids: getSelectedPayload(usinasValue),
            inversor_ids: getSelectedPayload(inversoresValue),
            site_ids: getSelectedPayload(sitesValue),
            empresa_ids: getSelectedPayload(empresasValue),
            incluir_inversores: incluirInversoresValue
        });

        const response = await fetchFlask('buscarFalhasBiss', body);

        setFilterOptions((previousOptions) => preserveSelfFilteredOptions({
            previousOptions,
            incomingOptions: response.filters,
            selectedUsinas: usinasValue,
            selectedInversores: inversoresValue,
            selectedSites: sitesValue,
            selectedEmpresas: empresasValue
        }));
        setChartRows(mapChartRows(response.chart));
        setChartInverterRows(mapChartInverterRows(response.chart_inversores));
        setInverterInfo(mapInverterInfo(response.inversores));
        setFalhas(mapFailureRows(response.falhas));
        setAlertas(mapAlertRows(response.alertas));
        setIsLoading(false);
    }, []);

    useEffect(() => {
        async function fetchInitialData () {
            if (initialFetchStarted.current) {
                return;
            }

            initialFetchStarted.current = true;

            await buscarFalhas({
                dataInicioValue: dataInicio,
                dataFimValue: dataFim,
                usinasValue: selectedUsinas,
                inversoresValue: selectedInversores,
                sitesValue: selectedSites,
                empresasValue: selectedEmpresas,
                incluirInversoresValue: incluirInversores
            });

            setInitialDataLoaded(true);
        }

        fetchInitialData();
    }, []);

    useEffect(() => {
        if (!initialDataLoaded) return;

        buscarFalhas({
            dataInicioValue: dataInicio,
            dataFimValue: dataFim,
            usinasValue: selectedUsinas,
            inversoresValue: selectedInversores,
            sitesValue: selectedSites,
            empresasValue: selectedEmpresas,
            incluirInversoresValue: incluirInversores
        });
    }, [dataFim, dataInicio, incluirInversores, selectedEmpresas, selectedInversores, selectedSites, selectedUsinas]);

    const handleDateRangeApply = (startDate, endDate) => {
        setDataInicio(startDate);
        setDataFim(endDate);
    };

    return (
        <Box className='mainBox falhas-biss-page'>
            <PageHeader>Geração x Falhas</PageHeader>

            <PageIntro eyebrow='BI SS' title='Geração x Falhas'>
                Geração, perda estimada e falhas por usina, inversor e site.
            </PageIntro>

            <Box className='falhas-biss-shell'>
                <Box className='falhas-biss-filters'>
                    <MonthRangePicker
                        startDate={dataInicio}
                        endDate={dataFim}
                        minDate={minMonth}
                        maxDate={getPreviousDayMonthStart()}
                        disabled={isLoading}
                        onApply={handleDateRangeApply}
                    />

                    <MultiSelectFilter
                        label='Usina'
                        labelId='falhas-biss-usina-filter'
                        options={usinaOptions}
                        value={selectedUsinas}
                        disabled={isLoading}
                        onChange={setSelectedUsinas}
                    />

                    <MultiSelectFilter
                        label='Inversor'
                        labelId='falhas-biss-inversor-filter'
                        options={inversorOptions}
                        value={selectedInversores}
                        disabled={isLoading}
                        blur={false}
                        onChange={setSelectedInversores}
                    />

                    <MultiSelectFilter
                        label='Site'
                        labelId='falhas-biss-site-filter'
                        options={siteOptions}
                        value={selectedSites}
                        disabled={isLoading}
                        onChange={setSelectedSites}
                    />

                    <MultiSelectFilter
                        label='SPE'
                        labelId='falhas-biss-spe-filter'
                        options={empresaOptions}
                        value={selectedEmpresas}
                        disabled={isLoading}
                        onChange={setSelectedEmpresas}
                    />
                </Box>

                <Box className='falhas-biss-summary'>
                    <Box>
                        <span><BoltRoundedIcon /> Geração</span>
                        <strong>{formatDecimal(summary.geracao)} kWh</strong>
                    </Box>
                    <Box className='is-loss'>
                        <span><WarningAmberRoundedIcon /> Perda</span>
                        <strong>{formatDecimal(summary.perda)} kWh</strong>
                    </Box>
                    <Box>
                        <span><AssignmentTurnedInRoundedIcon /> Falhas cadastradas</span>
                        <strong>{formatInteger(summary.falhas)}</strong>
                    </Box>
                    <Box className='is-alert'>
                        <span><ReportProblemRoundedIcon /> Alertas sem falha</span>
                        <strong>{formatInteger(summary.alertas)}</strong>
                    </Box>
                </Box>

                <Box className='falhas-biss-mode'>
                    <Box className='falhas-biss-mode-segment' role='tablist' aria-label='Modo de visualização'>
                        <button
                            type='button'
                            role='tab'
                            className={viewMode === 'consolidado' ? 'is-active' : ''}
                            aria-selected={viewMode === 'consolidado'}
                            disabled={isLoading}
                            onClick={() => setViewMode('consolidado')}
                        >
                            Consolidado
                        </button>
                        <button
                            type='button'
                            role='tab'
                            className={viewMode === 'comparar' ? 'is-active' : ''}
                            aria-selected={viewMode === 'comparar'}
                            disabled={isLoading}
                            onClick={() => setViewMode('comparar')}
                        >
                            Comparar inversores
                        </button>
                    </Box>
                    <span className='falhas-biss-mode-hint'>
                        {viewMode !== 'comparar' && <>Soma de tudo que está no filtro. Use <b>Comparar inversores</b> para ver cada equipamento separado.</>}
                        {viewMode === 'comparar' && !hasCompareScope && <>Selecione uma <b>usina</b> ou <b>inversores</b> no filtro para comparar.</>}
                        {viewMode === 'comparar' && hasCompareScope && <>Até <b>4 inversores</b> nos cards e gráficos, com cor fixa por inversor. O mapa de perdas mostra todos os inversores do filtro.</>}
                    </span>
                </Box>

                {viewMode === 'consolidado'
                    ? <FalhasBissChart rows={chartRows} />
                    : (
                        <FalhasBissCompare
                            rows={chartInverterRows}
                            inversores={inverterInfo}
                            hasScope={hasCompareScope}
                            dayRows={chartRows}
                            falhas={falhas}
                            alertas={alertas}
                            isLoading={isLoading}
                        />
                    )}

                <Box className='falhas-biss-tables'>
                    <Box className='falhas-biss-table-block'>
                        <Box className='falhas-biss-section-heading'>
                            <span><AssignmentTurnedInRoundedIcon /> Falhas cadastradas</span>
                        </Box>
                        <Box className='falhas-biss-table-panel'>
                            <DataGrid
                                className='falhas-biss-grid'
                                rows={falhas}
                                columns={failureColumns}
                                loading={isLoading}
                                disableRowSelectionOnClick
                                pageSizeOptions={[50, 100]}
                                initialState={{
                                    pagination: {
                                        paginationModel: {
                                            pageSize: 50
                                        }
                                    }
                                }}
                            />
                        </Box>
                    </Box>

                    <Box className='falhas-biss-table-block'>
                        <Box className='falhas-biss-section-heading'>
                            <span><ReportProblemRoundedIcon /> Alertas sem falha cadastrada</span>
                        </Box>
                        <Box className='falhas-biss-table-panel'>
                            <DataGrid
                                className='falhas-biss-grid'
                                rows={alertas}
                                columns={alertColumns}
                                loading={isLoading}
                                disableRowSelectionOnClick
                                pageSizeOptions={[50, 100]}
                                initialState={{
                                    pagination: {
                                        paginationModel: {
                                            pageSize: 50
                                        }
                                    }
                                }}
                            />
                        </Box>
                    </Box>
                </Box>
            </Box>
        </Box>
    );
};

export default FalhasBI;
